import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import Layout from '../components/Layout';

export default function Settings() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-ink-50">Settings</h1>
          <p className="text-ink-400 mt-1">Manage your account settings</p>
        </div>

        {/* Account Info */}
        <div className="card p-6 animate-slide-up">
          <h2 className="text-lg font-semibold text-ink-50 mb-4">Account Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-400 mb-1">Email</label>
              <p className="text-ink-100">{user?.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-400 mb-1">Role</label>
              <span className={user?.role === 'admin' ? 'badge-info' : 'badge-success'}>
                {user?.role}
              </span>
            </div>
            {user?.influencer && (
              <div>
                <label className="block text-sm font-medium text-ink-400 mb-1">Storefront</label>
                <p className="text-ink-100">{user.influencer.stage_name}</p>
                <p className="text-sm text-ink-500">
                  {user.influencer.first_name} {user.influencer.last_name}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="card p-6 animate-slide-up stagger-1">
          <h2 className="text-lg font-semibold text-ink-50 mb-4">Change Password</h2>

          <form onSubmit={handleChangePassword} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="p-3 rounded-lg bg-mint-500/10 border border-mint-500/20 text-mint-400 text-sm">
                {success}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
                required
                autoComplete="current-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                required
                autoComplete="new-password"
                minLength={8}
              />
              <p className="text-xs text-ink-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
                autoComplete="new-password"
              />
            </div>

            <div className="pt-2">
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
